import { lazy } from "react";
import { createBrowserRouter, Navigate } from "react-router-dom";
import { DEFAULT_LOCALE } from "@/i18n";
import LocaleLayout from "@/routes/LocaleLayout";
import NotFound from "@/routes/NotFound";
import PortalGuard from "@/routes/guards/PortalGuard";
import AdminGuard from "@/routes/guards/AdminGuard";

// Route-level code splitting (CE-14 P3). Every page is its own lazy chunk, so
// the heavy per-page deps load only with their route instead of inflating the
// initial bundle: recharts (portal/monitoring), the ~15k-line known-entities
// DB (explore/address), the dashboard chart suite (dashboard). The shell
// (LocaleLayout) and auth guards stay eager; the <Suspense> boundary in
// LocaleLayout covers every lazy child while its chunk loads.
const Hello = lazy(() => import("@/routes/Hello"));
const PortalIndex = lazy(() => import("@/routes/portal/PortalIndex"));
const AdminPage = lazy(() => import("@/routes/admin/index"));
const AdminLogin = lazy(() => import("@/routes/admin/login"));
const WalletProfiler = lazy(() => import("@/routes/tools/wallet-profiler"));
const PathFinder = lazy(() => import("@/routes/tools/path-finder"));
const ScamDetector = lazy(() => import("@/routes/tools/scam-detector"));
const TxDecoder = lazy(() => import("@/routes/tools/tx-decoder"));
const Status = lazy(() => import("@/routes/status"));
const CheckoutSuccess = lazy(() => import("@/routes/checkout-success"));
const MyReports = lazy(() => import("@/routes/my-reports"));
const Login = lazy(() => import("@/routes/portal/login"));
const Signup = lazy(() => import("@/routes/portal/signup"));
const AcceptInvite = lazy(() => import("@/routes/portal/accept-invite"));
const ForgotPassword = lazy(() => import("./routes/portal/forgot-password"));
const ResetPassword = lazy(() => import("./routes/portal/reset-password"));
const ApiKeys = lazy(() => import("@/routes/portal/api-keys"));
const Playground = lazy(() => import("@/routes/portal/playground"));
const Investigations = lazy(() => import("./routes/portal/investigations"));
const Compliance = lazy(() => import("@/routes/portal/compliance"));
const Monitoring = lazy(() => import("@/routes/portal/monitoring"));
const Settings = lazy(() => import("@/routes/portal/settings"));
const Security = lazy(() => import("@/routes/portal/security"));
const Team = lazy(() => import("@/routes/portal/team"));
const Audit = lazy(() => import("@/routes/portal/audit"));
const Retention = lazy(() => import("@/routes/portal/retention"));
const TravelRule = lazy(() => import("@/routes/portal/travel-rule"));
const TravelRuleSettings = lazy(() => import("@/routes/portal/travel-rule-settings"));
const Screening = lazy(() => import("@/routes/portal/screening"));
const ScreeningPolicies = lazy(() => import("@/routes/portal/screening/policies"));
const ScreeningQueue = lazy(() => import("@/routes/portal/screening/queue"));
const ScreeningResults = lazy(() => import("@/routes/portal/screening/results"));
const ScreeningResultDetail = lazy(() => import("./routes/portal/screening/results/detail"));
const Customers = lazy(() => import("@/routes/portal/customers"));
const CustomerDetail = lazy(() => import("@/routes/portal/customers/detail"));
const Billing = lazy(() => import("@/routes/portal/billing"));
const Usage = lazy(() => import("@/routes/portal/usage"));
const IntelAddress = lazy(() => import("@/routes/portal/intel/address"));
const IntelTransaction = lazy(() => import("@/routes/portal/intel/transaction"));
const IntelPathFind = lazy(() => import("@/routes/portal/intel/path-find"));
const IntelDomain = lazy(() => import("@/routes/portal/intel/domain"));
const PublicInvestigation = lazy(() => import("@/routes/investigate/public"));
const InvestigateDetail = lazy(() => import("@/routes/investigate/detail"));
const Investigate = lazy(() => import("@/routes/investigate/index"));
const Dashboard = lazy(() => import("@/routes/dashboard"));
const Explore = lazy(() => import("@/routes/explore/index"));
const TxExplorer = lazy(() => import("@/routes/explore/tx"));
const AddressExplorer = lazy(() => import("@/routes/explore/address"));
const Report = lazy(() => import("@/routes/report"));
const Filing = lazy(() => import("@/routes/filing"));

/**
 * App-surface router. LocaleLayout hosts the /:locale shell + the Suspense
 * boundary; every page below is a lazy chunk (see the import block above).
 */
export const router = createBrowserRouter([
  { path: "/", element: <Navigate to={`/${DEFAULT_LOCALE}`} replace /> },
  {
    path: "/:locale",
    element: <LocaleLayout />,
    children: [
      { index: true, element: <Hello /> },
      // Authed portal (ce_org via PortalGuard).
      {
        path: "portal",
        element: <PortalGuard />,
        children: [
          { index: true, element: <PortalIndex /> },
          { path: "api-keys", element: <ApiKeys /> },
          { path: "playground", element: <Playground /> },
          // CE-47 — org investigations (submit, list, result viewer).
          { path: "investigations", element: <Investigations /> },
          { path: "compliance", element: <Compliance /> },
          { path: "monitoring", element: <Monitoring /> },
          { path: "settings", element: <Settings /> },
          // CE-30 — own second factor for any role; the org-wide SSO/MFA
          // controls inside the page render only for OWNER/ADMIN, matching the
          // endpoints' own gates.
          { path: "security", element: <Security /> },
          { path: "team", element: <Team /> },
          // CE-31 — org audit trail. Route is not role-gated client-side; the
          // endpoint is OWNER/ADMIN-only and the page renders its own 403 state.
          { path: "audit", element: <Audit /> },
          // CE-33 — retention periods + legal hold. Same shape as audit: the
          // endpoint enforces OWNER/ADMIN for writes, and the page renders
          // read-only for everyone else rather than 404ing.
          { path: "retention", element: <Retention /> },
          // Travel Rule (EU TFR) — restored after CE-14's P4 prune dropped the
          // unported Next page (CE-23). Both routes drive the surviving
          // /api/org/portal/travel-rule/* endpoints.
          { path: "travel-rule", element: <TravelRule /> },
          { path: "travel-rule/settings", element: <TravelRuleSettings /> },
          // CE-25 — restored KYT screening + customers sections (verbatim SPA port).
          { path: "screening", element: <Screening /> },
          { path: "screening/policies", element: <ScreeningPolicies /> },
          { path: "screening/queue", element: <ScreeningQueue /> },
          { path: "screening/results", element: <ScreeningResults /> },
          // CE-49 follow-up — single-verdict record, hosts the fund-flow graph.
          { path: "screening/results/:id", element: <ScreeningResultDetail /> },
          { path: "customers", element: <Customers /> },
          { path: "customers/:id", element: <CustomerDetail /> },
          // CE-26 — billing renders BillingClient from the new /billing/overview endpoint.
          { path: "billing", element: <Billing /> },
          // CE-138 — calls and spend by day and endpoint from /api/org/portal/usage (CE-134).
          { path: "usage", element: <Usage /> },
          // CE-131 — address intelligence through the cookie-authed
          // /api/org/portal/intel/* routes (CE-129), charged per lookup.
          { path: "intel/address", element: <IntelAddress /> },
          // CE-132 — transaction decode/analysis and path-find, same seam.
          { path: "intel/transaction", element: <IntelTransaction /> },
          { path: "intel/path-find", element: <IntelPathFind /> },
          // CE-133 — domain intelligence, same seam.
          { path: "intel/domain", element: <IntelDomain /> },
        ],
      },
      // Public portal auth pages (NOT behind the guard).
      { path: "portal/login", element: <Login /> },
      { path: "portal/signup", element: <Signup /> },
      { path: "portal/accept-invite", element: <AcceptInvite /> },
      // CE-48 — password recovery; there was no reset path before.
      { path: "portal/forgot-password", element: <ForgotPassword /> },
      { path: "portal/reset-password", element: <ResetPassword /> },
      {
        path: "admin",
        element: <AdminGuard />,
        children: [{ index: true, element: <AdminPage /> }],
      },
      { path: "admin/login", element: <AdminLogin /> },
      // P1 Wave A — public tools (ported verbatim from the Next app).
      { path: "tools/wallet-profiler", element: <WalletProfiler /> },
      { path: "tools/path-finder", element: <PathFinder /> },
      { path: "tools/scam-detector", element: <ScamDetector /> },
      { path: "tools/tx-decoder", element: <TxDecoder /> },
      { path: "status", element: <Status /> },
      { path: "checkout/success", element: <CheckoutSuccess /> },
      { path: "my-reports", element: <MyReports /> },
      { path: "investigate", element: <Investigate /> },
      { path: "investigate/:id/public", element: <PublicInvestigation /> },
      { path: "investigate/:id", element: <InvestigateDetail /> },
      { path: "dashboard", element: <Dashboard /> },
      { path: "explore", element: <Explore /> },
      { path: "explore/tx/:hash", element: <TxExplorer /> },
      { path: "explore/address/:address", element: <AddressExplorer /> },
      { path: "report/:id", element: <Report /> },
      { path: "filing/:id", element: <Filing /> },
      { path: "*", element: <NotFound /> },
    ],
  },
  { path: "*", element: <NotFound /> },
]);

export default router;
